import axios, { AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { BASE_URL, SUBSCRIBE_TO_TOPIC_URL, ADMIN_LOGIN_URL, ADMIN_GMAIL_LOGIN_URL } from "../constants/endpoints";
import { logout } from "../utils/logout";
import { message } from "antd";
import { getSession, setSession } from "../utils/session";
import { getFirebaseIdToken, getFirebaseUserId } from "../utils/firebaseAuth";
import * as Sentry from "@sentry/react";

declare module "axios" {
  interface AxiosRequestConfig {
    /**
     * The caller handles failures itself (e.g. a retry loop). Skips the global
     * error toasts and Sentry capture in the response interceptor. Auth
     * handling (401 token refresh / logout) still applies.
     */
    handleErrorLocally?: boolean;
  }
}

const axiosClient = axios.create({
  baseURL: BASE_URL,
});

// URLs that should skip auth token injection (auth-related endpoints)
const AUTH_ENDPOINTS = [ADMIN_LOGIN_URL, ADMIN_GMAIL_LOGIN_URL];

// Timeout for waiting for auth token (10 seconds)
const AUTH_TOKEN_TIMEOUT_MS = 10000;

// Inactivity timeout - logout user after 16 hours of no API activity
const INACTIVITY_TIMEOUT_MS = 16 * 60 * 60 * 1000; // 16 hours

interface GlobalRequestAX {
  cache: Map<string, any>;
}

declare global {
  var requestAX: GlobalRequestAX;
}

globalThis.requestAX = {
  cache: new Map<string, any>(),
};

const getRequestKey = (config: AxiosRequestConfig) => {
  return `${config.method}-${config.url}-${JSON.stringify(config.params)}-${JSON.stringify(config.data)}`;
};

// Track if we're currently logging out to prevent multiple logout calls
let isLoggingOut = false;

/**
 * Handle auth failure - logout user
 */
const handleAuthFailure = async (reason: string) => {
  if (isLoggingOut) return;
  isLoggingOut = true;
  
  Sentry.captureMessage(`Auth failure in axios: ${reason}`, {
    level: "warning",
    tags: { auth_flow: "axios_auth_failure" },
  });
  
  await logout();
  isLoggingOut = false;
};

axiosClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Check if this is an auth endpoint that doesn't need token
    const isAuthEndpoint = AUTH_ENDPOINTS.some(endpoint => config.url?.includes(endpoint));
    const isOnLoginPage = window.location.pathname === "/login";
    
    if (!isAuthEndpoint) {
      // Check for inactivity timeout (skip on login page)
      if (!isOnLoginPage) {
        const lastRequestTimeStr = getSession("lastRequestTime");
        if (lastRequestTimeStr) {
          const lastRequestTime = parseInt(lastRequestTimeStr, 10);
          const currentTime = Date.now();
          const timeSinceLastRequest = currentTime - lastRequestTime;
          
          if (timeSinceLastRequest > INACTIVITY_TIMEOUT_MS) {
            Sentry.captureMessage("User logged out due to inactivity", {
              level: "info",
              tags: { auth_flow: "inactivity_logout" },
              extra: { 
                inactiveHours: Math.round(timeSinceLastRequest / (60 * 60 * 1000)),
                lastRequestTime: new Date(lastRequestTime).toISOString(),
              },
            });
            
            // Logout and abort the request
            await logout();
            const controller = new AbortController();
            controller.abort();
            config.signal = controller.signal;
            return config;
          }
        }
      }
      
      // Wait for Firebase auth and get token
      const startTime = Date.now();
      
      try {
        // Get ID token from Firebase SDK (auto-refreshes if expired)
        const idToken = await getFirebaseIdToken(false, AUTH_TOKEN_TIMEOUT_MS);
        const userId = await getFirebaseUserId();
        
        if (idToken && userId) {
          config.headers["x-auth-token"] = idToken;
          config.headers["firebase-user-id"] = userId;
        } else {
          if (!isOnLoginPage) {
            // Not on login page and no token - this is an auth failure
            const elapsed = Date.now() - startTime;
            
            if (elapsed >= AUTH_TOKEN_TIMEOUT_MS) {
              // Timeout waiting for token
              handleAuthFailure("Token fetch timeout");
            } else {
              // No user authenticated
              handleAuthFailure("No authenticated user");
            }
            // Cancel the request
            const controller = new AbortController();
            controller.abort();
            config.signal = controller.signal;
          }
          // If on login page, let the request proceed without token
        }
      } catch (error) {
        Sentry.captureException(error, {
          level: "error",
          tags: { auth_flow: "axios_token_fetch" },
        });
        
        // Don't block the request on login page
        if (!isOnLoginPage) {
          handleAuthFailure("Token fetch error");
        }
      }
      
      // Update last request time for activity tracking
      setSession("lastRequestTime", Date.now().toString());
    }

    // Duplicate request prevention
    const requestKey = getRequestKey(config);

    if (requestAX.cache.has(requestKey)) {
      config.cancelToken = new axios.CancelToken((cancel) =>
        cancel("Duplicate request canceled")
      );
    } else {
      config.cancelToken = new axios.CancelToken((cancel) => {
        requestAX.cache.set(requestKey, cancel);
        setTimeout(() => {
          requestAX.cache.delete(requestKey);
        }, 300);
      });
    }

    return config;
  },
  (error) => {
    console.error("request error");
    Sentry.captureException(error);
    return Promise.reject(error);
  }
);

axiosClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const requestURL = originalRequest?.url;

    // Skip error messages for topic subscription endpoint
    if (requestURL === SUBSCRIBE_TO_TOPIC_URL) {
      Sentry.captureException(error);
      return Promise.reject(error);
    }

    // Handle aborted requests (from our auth failure handling)
    if (axios.isCancel(error) || error.name === "CanceledError") {
      return Promise.reject(error);
    }

    const handleErrorLocally = Boolean(originalRequest?.handleErrorLocally);

    if (error.code === "ERR_NETWORK") {
      if (!handleErrorLocally) {
        message.error(error.message);
        Sentry.captureException(error);
      }
      return Promise.reject(error);
    }

    // Check if error.response exists before destructuring
    if (!error.response) {
      if (!handleErrorLocally) {
        Sentry.captureException(error);
      }
      return Promise.reject(error);
    }

    const response: AxiosResponse = error.response;
    const { data, status } = response;

    const errorMessage = data?.displayMessage;
    const responseURL = response.config.url;

    if (responseURL?.includes("login")) {
      if (status === 500 || status === 401 || status == 400) {
        if (errorMessage) {
          message.error(errorMessage);
        } else {
          message.error("Login failed: Incorrect email id and password");
        }
      } else {
        message.error(
          "An error occurred during login. Please try again later."
        );
      }

      Sentry.captureException(error);
      return Promise.reject(error);
    }

    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        // Try to get a fresh token from Firebase (force refresh)
        const idToken = await getFirebaseIdToken(true, AUTH_TOKEN_TIMEOUT_MS);
        
        if (idToken) {
          originalRequest.headers["x-auth-token"] = idToken;
          return axiosClient(originalRequest);
        }
      } catch (refreshError) {
        Sentry.captureException(refreshError, {
          level: "warning",
          tags: { auth_flow: "token_refresh_failed" },
        });
      }
      
      // If we get here, refresh failed - logout
      await logout();
      return Promise.reject(error);
    }

    originalRequest._retry = false;

    if (handleErrorLocally) {
      if (status === 401) {
        await logout();
      }
      return Promise.reject(error);
    }

    switch (status) {
      case 401:
        await logout();
        break;
      case 400:
        message.error(errorMessage);
        break;
      case 403:
        message.error(errorMessage ?? "User cannot perform this operation");
        break;
      case 404:
        message.error(errorMessage ?? "Not found");
        break;
      case 422:
        message.error(errorMessage ?? "Something went wrong");
        break;
      case 500:
        message.error(errorMessage ?? "Something went wrong");
        break;
      default:
        break;
    }

    Sentry.captureException(error);
    return Promise.reject(error);
  }
);

export default axiosClient;
